import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/fp/isEmpty';

import { PATH } from '../config';
import storage from '../service/storage';
import Client from '../services/client';
import { useBootStraps } from '../services/use-bootstraps';

interface DenDenWrapperProps {
  children: React.ReactNode;
}

const ALLOWED_PATHS = [
  PATH.AUTH,
  PATH.PAYMENT,
  PATH.PAYMENT_SETTING,
  PATH.PAYMENT_AUTH,
];
const AUTH_REQUIRED_PATHS = [PATH.PAYMENT, PATH.PAYMENT_SETTING];

const checkIsAllowedPath = (path: string) =>
  ALLOWED_PATHS.some((p) => path.includes(p));

export const checkIsAuthRequiredPath = (path: string) =>
  AUTH_REQUIRED_PATHS.some((p) => path.includes(p));

export const DenDenWrapper = ({ children }: DenDenWrapperProps) => {
  useBootStraps();
  const [hasCheckRouting, setHasCheckRouting] = useState<boolean>(false);
  const { asPath, replace, push, query } = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkIfBindingIsSucceeded = async () => {
      if (isLoading) return;

      const hash = new URLSearchParams(asPath.split('#')[1]);
      const result = hash.get('result');

      if (result !== 'success') {
        replace(PATH.PAYMENT, { query: { bindingResult: 'fail' } });
        return;
      }

      const token = hash.get('token');
      const transactionId = hash.get('merchant_transaction_id');

      setIsLoading(true);

      try {
        await Client.createTransactionToken({
          token,
          transactionId,
        });

        queryClient.invalidateQueries({ queryKey: ['me'] });

        await replace(PATH.PAYMENT, { query: { bindingResult: 'success' } });
      } catch (err) {
        console.log(err);
        await replace(PATH.PAYMENT, { query: { bindingResult: 'fail' } });
      }

      setIsLoading(false);
    };

    const navigateToAuth = async () => {
      await push({
        pathname: PATH.AUTH,
        query: {
          ...query,
          prevPath: asPath,
        },
      });
      setHasCheckRouting(true);
    };

    async function checkPath() {
      if (hasCheckRouting) return;

      if (!checkIsAllowedPath(asPath)) {
        await replace(PATH.EXPIRED);
        setHasCheckRouting(true);
        return;
      }

      if (asPath.includes(PATH.PAYMENT_AUTH)) {
        await checkIfBindingIsSucceeded();
        setHasCheckRouting(true);
        return;
      }

      const oneTimeToken = query.OneTimeToken as string | undefined;

      // If oneTimeToken exists, redirect to auth page
      if (oneTimeToken) {
        await navigateToAuth();
        return;
      }

      const auth = storage.getAuth();

      if (!isEmpty(auth)) {
        // FIXME: This is a weird workaround to fix the problem that the page gets 404 while first accessing the page
        await replace(asPath, asPath);
        setHasCheckRouting(true);
        return;
      }

      // If the path is auth required, redirect to auth page
      if (checkIsAuthRequiredPath(asPath)) {
        await navigateToAuth();
        return;
      }

      // FIXME: This is a weird workaround to fix the problem that the page gets 404 while first accessing the page
      replace(asPath);
      setHasCheckRouting(true);
    }

    checkPath();
  }, [asPath, push, replace, hasCheckRouting, query, queryClient, isLoading]);

  if (!hasCheckRouting) {
    return null;
  }

  return children;
};
